/**
 * Field rules mirroring the server's own constraints, so a form never accepts input
 * the API will reject. The server stays the authority — these run first to give an
 * answer without a round trip, and server `fieldErrors` still render on the same
 * fields when the two ever disagree.
 *
 * Sources: AuthDtos / AdminUserDtos (@Size, @Pattern), PasswordPolicy,
 * Identifiers (mobile and email regexes), StorageProperties (upload limits).
 */

// Identifiers.java uses exactly these two patterns.
const MOBILE_RE = /^[6-9][0-9]{9}$/;
const EMAIL_RE = /^[^@\s]+@[^@\s.]+\.[^@\s]{2,}$/;
// AuthDtos uses exactly this: six digits, and an Indian PIN never starts with zero.
const POSTAL_RE = /^[1-9][0-9]{5}$/;

export const LIMITS = {
  fullName: { min: 2, max: 100 },
  // The registration form asks for a name in two parts; `fullName` stays the display
  // form the server derives from them.
  firstName: { min: 1, max: 60 },
  lastName: { max: 60 },
  area: { max: 120 },
  // The City Master's column width, for the "Other" box on the registration forms.
  city: { max: 120 },
  affiliationCode: { max: 40 },
  postalCode: { digits: 6 },
  email: { max: 255 },
  password: { min: 8, max: 100 },
  designation: { max: 120 },
  // Ten digits and no more: the field takes an Indian mobile number, and a field that
  // accepts fifteen characters is a field that lets someone type a wrong one.
  mobile: { digits: 10 },
  // A count, not a length — subjects are picked from the master, so their names are
  // already the master's problem.
  subjects: { max: 20 },
  schoolName: { max: 200 },
  otp: { length: 6 },
  idProof: {
    maxBytes: 5 * 1024 * 1024,
    types: ['application/pdf', 'image/jpeg', 'image/png'],
    label: 'PDF, JPG or PNG up to 5 MB',
  },
};

export const PASSWORD_HINT = 'At least 8 characters, including one uppercase letter and one number';

/** Strips spaces, dashes and a +91 / 0 prefix, matching Identifiers.normalizeMobile. */
export function normalizeMobile(raw) {
  if (!raw) return '';
  let digits = String(raw).replace(/[^0-9]/g, '');
  if (digits.length > 10 && digits.startsWith('91')) digits = digits.slice(-10);
  else if (digits.length === 11 && digits.startsWith('0')) digits = digits.slice(1);
  return digits;
}

export const normalizeEmail = (raw) => (raw ? String(raw).trim().toLowerCase() : '');

const blank = (value) => value === undefined || value === null || String(value).trim() === '';

/** Runs validators in order and returns the first message, or null when all pass. */
const chain =
  (...validators) =>
  (value, values) => {
    for (const validate of validators) {
      const message = validate(value, values);
      if (message) return message;
    }
    return null;
  };

export const required =
  (label = 'This field') =>
  (value) =>
    blank(value) ? `${label} is required` : null;

export const optional = (validate) => (value, values) => (blank(value) ? null : validate(value, values));

const length = (label, { min, max }) => (value) => {
  const text = String(value).trim();
  if (min && text.length < min) return `${label} must be at least ${min} characters`;
  if (max && text.length > max) return `${label} must be ${max} characters or fewer`;
  return null;
};

// ── the fields the app actually collects ────────────────────────────────────

export const fullName = chain(required('Full name'), length('Full name', LIMITS.fullName));

export const mobile = chain(required('Mobile number'), (value) =>
  MOBILE_RE.test(normalizeMobile(value))
    ? null
    : 'Enter a valid 10-digit mobile number starting with 6, 7, 8 or 9',
);

export const optionalMobile = optional(mobile);

/** The student form's only number, so its message names the field the label shows. */
export const parentMobile = chain(required("Parent's mobile number"), (value) =>
  MOBILE_RE.test(normalizeMobile(value))
    ? null
    : 'Enter a valid 10-digit mobile number starting with 6, 7, 8 or 9',
);

/**
 * Props for a mobile-number input.
 *
 * <p>Everything about the field in one place, because the rule and the input have to
 * agree: ten digits, capped by `maxLength`, and anything else the browser lets through —
 * a paste of "+91 98765 43210", a typed space — normalised away on the way in rather
 * than surviving to be rejected on submit.
 */
export const mobileField = (form, name = 'mobile') =>
  form.field(name, {
    inputMode: 'numeric',
    autoComplete: 'tel',
    maxLength: LIMITS.mobile.digits,
    placeholder: '9876543210',
    onChange: (event) =>
      form.setValue(name, normalizeMobile(event.target.value).slice(0, LIMITS.mobile.digits)),
  });

const emailFormat = (value) =>
  EMAIL_RE.test(normalizeEmail(value)) ? null : 'Enter a valid email address';

export const email = chain(required('Email address'), emailFormat, length('Email address', LIMITS.email));

export const optionalEmail = optional(chain(emailFormat, length('Email address', LIMITS.email)));

export const password = chain(required('Password'), (value) => {
  const text = String(value);
  if (text.length < LIMITS.password.min || text.length > LIMITS.password.max) return PASSWORD_HINT;
  if (!/[A-Z]/.test(text)) return PASSWORD_HINT;
  if (!/[0-9]/.test(text)) return PASSWORD_HINT;
  return null;
});

export const currentPassword = required('Current password');

/** The Login and Forgot screens take one field that may hold either form. */
export const identifier = chain(required('Mobile number or email'), (value) => {
  const text = String(value).trim();
  if (text.includes('@')) return emailFormat(text);
  return MOBILE_RE.test(normalizeMobile(text))
    ? null
    : 'Enter a valid 10-digit mobile number or an email address';
});

export const otpCode = chain(required('Code'), (value) =>
  new RegExp(`^[0-9]{${LIMITS.otp.length}}$`).test(String(value))
    ? null
    : `Enter the ${LIMITS.otp.length}-digit code`,
);

export const firstName = chain(required('First name'), length('First name', LIMITS.firstName));
export const lastName = optional(length('Last name', LIMITS.lastName));
export const area = optional(length('Area', LIMITS.area));
export const affiliationCode = optional(length('Affiliation code', LIMITS.affiliationCode));

export const postalCode = optional((value) =>
  POSTAL_RE.test(String(value).trim()) ? null : `Enter a valid ${LIMITS.postalCode.digits}-digit postal code`,
);

/** Digits only, capped at six — the same shape as the mobile field, for the same reason. */
export const postalField = (form, name = 'postalCode') =>
  form.field(name, {
    inputMode: 'numeric',
    maxLength: LIMITS.postalCode.digits,
    placeholder: '560001',
    onChange: (event) =>
      form.setValue(name, String(event.target.value).replace(/[^0-9]/g, '').slice(0, LIMITS.postalCode.digits)),
  });

/**
 * The form's Re-enter Password. A cross-field rule rather than a validator, because it
 * depends on the other field and has to re-run when either of them changes.
 */
export const passwordsMatch = (values) =>
  values.confirmPassword && values.password !== values.confirmPassword
    ? { confirmPassword: 'The two passwords do not match' }
    : {};

export const confirmPassword = required('Re-entered password');

export const schoolName = optional(length('School name', LIMITS.schoolName));
export const designation = optional(length('Designation', LIMITS.designation));

export const idProof =
  ({ requiredFile = true } = {}) =>
  (file) => {
    if (!file) return requiredFile ? 'An ID proof document is required' : null;
    if (!LIMITS.idProof.types.includes(file.type)) {
      return `Unsupported file type. Upload a ${LIMITS.idProof.label}.`;
    }
    if (file.size > LIMITS.idProof.maxBytes) return 'That file is over 5 MB. Choose a smaller one.';
    return null;
  };
